import React, { useState, useEffect, useMemo } from 'react';
import {
	Card,
	Row,
	Col,
	DatePicker,
	Typography,
	Spin,
	Space,
	Tag,
	List,
	Statistic,
	Tooltip,
	Empty,
	Collapse,
	Alert,
} from 'antd';
import { Line } from '@ant-design/plots';
import {
	MessageOutlined,
	RiseOutlined,
	FallOutlined,
	FireOutlined,
	QuestionCircleOutlined,
} from '@ant-design/icons';
import { Dayjs } from 'dayjs';
import apiClient from '@/utils/apiClient';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface TrendData {
	date: string;
	count: number;
}
interface SampleConversation {
	id: string;
	snippet: string;
	link: string;
}
interface SentimentBreakdown {
	positive: number;
	neutral: number;
	negative: number;
}

interface TopicCluster {
	topic_name: string;
	total_count: number;
	trend_data: TrendData[];
	sample_conversations: SampleConversation[];
	sentiment_breakdown: SentimentBreakdown;
	most_recent_day: string;
}

interface KeyInsight {
	value: number;
	change: number;
}

interface InsightSummary {
	totalConversations: KeyInsight;
	topTrending: { name: string; change: number } | null;
	topBugReports: KeyInsight | null;
}

const SampleMessage: React.FC<{ convo: SampleConversation }> = ({ convo }) => (
	<Tooltip title="Open conversation in Intercom">
		<a
			href={convo.link}
			target="_blank"
			rel="noopener noreferrer"
			style={{ color: 'inherit', display: 'flex', alignItems: 'flex-start', width: '100%' }}
		>
			<Text type="secondary" style={{ whiteSpace: 'normal' }}>
				- {convo.snippet}
			</Text>
		</a>
	</Tooltip>
);

const IntercomTrends: React.FC = () => {
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);
	const [allTopics, setAllTopics] = useState<TopicCluster[]>([]);
	const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>([
		dayjs().subtract(29, 'd'),
		dayjs(),
	]);

	const [summaryData, setSummaryData] = useState<InsightSummary | null>(null);
	const [loadingSummary, setLoadingSummary] = useState<boolean>(true);
	const [hiddenTopics, setHiddenTopics] = useState<Set<string>>(new Set());

	useEffect(() => {
		const fetchAllData = async () => {
			if (!dateRange || !dateRange[0] || !dateRange[1]) {
				return;
			}

			setLoading(true);
			setLoadingSummary(true);
			setError(null);

			try {
				const [currentStart, currentEnd] = dateRange;
				const duration = currentEnd.diff(currentStart, 'day');
				const previousEnd = currentStart.subtract(1, 'day');
				const previousStart = previousEnd.subtract(duration, 'day');

				const [currentRes, previousRes] = await Promise.all([
					apiClient.request({
						endPoint: '/api/admin/topics',
						method: 'GET',
						params: {
							startDate: currentStart.format('YYYY-MM-DD'),
							endDate: currentEnd.format('YYYY-MM-DD'),
						},
						clientType: 'ADMIN',
					}),
					apiClient.request({
						endPoint: '/api/admin/topics',
						method: 'GET',
						params: {
							startDate: previousStart.format('YYYY-MM-DD'),
							endDate: previousEnd.format('YYYY-MM-DD'),
						},
						clientType: 'ADMIN',
					}),
				]);

				const currentTopics: TopicCluster[] = currentRes.data || [];
				const previousTopics: TopicCluster[] = previousRes.data || [];

				setAllTopics(currentTopics);

				const getPercentageChange = (current: number, previous: number): number => {
					if (previous === 0) {
						return current > 0 ? 100 : 0;
					}
					return ((current - previous) / previous) * 100;
				};

				const currentTotal = currentTopics.reduce((sum, t) => sum + t.total_count, 0);
				const previousTotal = previousTopics.reduce((sum, t) => sum + t.total_count, 0);

				const currentBugReports =
					currentTopics.find((t) => t.topic_name === 'Technical Issue / Bug Report')?.total_count ||
					0;
				const previousBugReports =
					previousTopics.find((t) => t.topic_name === 'Technical Issue / Bug Report')
						?.total_count || 0;

				const topicChanges = currentTopics
					.map((currentTopic) => {
						const previousTopic = previousTopics.find(
							(pt) => pt.topic_name === currentTopic.topic_name,
						);
						const change = getPercentageChange(
							currentTopic.total_count,
							previousTopic?.total_count || 0,
						);
						return { name: currentTopic.topic_name, change };
					})
					.sort((a, b) => b.change - a.change);

				const summary: InsightSummary = {
					totalConversations: {
						value: currentTotal,
						change: getPercentageChange(currentTotal, previousTotal),
					},
					topBugReports: {
						value: currentBugReports,
						change: getPercentageChange(currentBugReports, previousBugReports),
					},
					topTrending:
						topicChanges.length > 0 && topicChanges[0].change > 0 ? topicChanges[0] : null,
				};
				setSummaryData(summary);
			} catch (err) {
				setError(err instanceof Error ? err.message : 'Failed to load Intercom topics.');
			} finally {
				setLoading(false);
				setLoadingSummary(false);
			}
		};

		fetchAllData();
	}, [dateRange]);

	const allTopicsChartData = useMemo(() => {
		return allTopics
			.filter((topic) => !hiddenTopics.has(topic.topic_name))
			.flatMap((topic) => topic.trend_data.map((td) => ({ ...td, topic: topic.topic_name })));
	}, [allTopics, hiddenTopics]);

	const topicStats = useMemo(() => {
		return allTopics.reduce(
			(acc, topic) => {
				const totalCount = topic.total_count;
				const avgDaily =
					topic.trend_data.length > 0 ? (totalCount / topic.trend_data.length).toFixed(1) : '0';
				const peakDay = topic.trend_data.reduce((max, day) => (day.count > max.count ? day : max), {
					count: 0,
					date: '',
				});

				acc[topic.topic_name] = {
					total: totalCount,
					avgDaily,
					peakCount: peakDay.count,
					peakDate: peakDay.date,
				};
				return acc;
			},
			{} as Record<
				string,
				{ total: number; avgDaily: string; peakCount: number; peakDate: string }
			>,
		);
	}, [allTopics]);

	const renderChange = (change: number) => {
		const isInfinite = !isFinite(change);
		const value = isInfinite ? 100 : change;
		const color = value >= 0 ? '#3f8600' : '#cf1322';
		const icon = value >= 0 ? <RiseOutlined /> : <FallOutlined />;
		return (
			<span style={{ color, marginLeft: 8 }}>
				{icon} {isInfinite ? 'New' : `${value.toFixed(1)}%`}
			</span>
		);
	};

	const toggleTopic = (topicName: string) => {
		setHiddenTopics((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(topicName)) {
				newSet.delete(topicName);
			} else {
				newSet.add(topicName);
			}
			return newSet;
		});
	};

	return (
		<Space direction="vertical" size="large" style={{ width: '100%' }}>
			<Title level={2}>Intercom Conversation Trends</Title>
			<Card>
				<Text strong>Filter by Date Range:</Text>
				<br />
				<RangePicker
					value={dateRange}
					onChange={(dates) => setDateRange(dates as [Dayjs, Dayjs] | null)}
				/>
			</Card>
			{error && (
				<Alert
					message="Error"
					description={error}
					type="error"
					showIcon
					closable
					onClose={() => setError(null)}
				/>
			)}

			<Card
				title={
					<Space align="center">
						<Text>📊 Key Insights</Text>
						<Tooltip title="A high-level summary of key metrics compared to the previous period of the same duration.">
							<QuestionCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
						</Tooltip>
					</Space>
				}
			>
				<Spin spinning={loadingSummary}>
					{summaryData && (
						<Row gutter={[32, 16]}>
							<Col xs={24} sm={12} md={8}>
								<Statistic
									title={
										<Space>
											<Text>Total Conversations</Text>
											<Tooltip title="Total conversations across all topics.">
												<QuestionCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
											</Tooltip>
										</Space>
									}
									value={summaryData.totalConversations.value}
									prefix={<MessageOutlined />}
								/>
								{renderChange(summaryData.totalConversations.change)}
							</Col>
							<Col xs={24} sm={12} md={8}>
								<Statistic
									title={
										<Space>
											<Text>Bug Reports</Text>
											<Tooltip title="Conversations categorized as a 'Technical Issue / Bug Report'.">
												<QuestionCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
											</Tooltip>
										</Space>
									}
									value={summaryData.topBugReports?.value}
									prefix={<FireOutlined />}
								/>
								{summaryData.topBugReports && renderChange(summaryData.topBugReports.change)}
							</Col>
							<Col xs={24} sm={12} md={8}>
								<Title level={5}>
									<Space>
										<Text>🔥 Top Trending Topic</Text>
										<Tooltip title="The topic with the largest percentage increase in volume.">
											<QuestionCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
										</Tooltip>
									</Space>
								</Title>
								{summaryData.topTrending ? (
									<>
										<Text>{summaryData.topTrending.name}</Text>
										{renderChange(summaryData.topTrending.change)}
									</>
								) : (
									<Text type="secondary">No significant increases</Text>
								)}
							</Col>
						</Row>
					)}
				</Spin>
			</Card>

			{/* ... Conversation Volume Card (remains the same) ... */}
			<Card
				title={
					<Space align="center">
						<Text>📈 Conversation Volume Over Time</Text>
						<Tooltip title="Shows the daily conversation volume for all topics. Click items in the legend to show or hide a topic.">
							<QuestionCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
						</Tooltip>
					</Space>
				}
			>
				<Spin spinning={loading}>
					<div style={{ marginTop: '20px' }}>
						{/* Custom Legend with Tooltips */}
						<div
							style={{
								display: 'flex',
								justifyContent: 'flex-end',
								flexWrap: 'wrap',
								gap: '12px',
								marginBottom: '20px',
							}}
						>
							{allTopics.map((topic, index) => {
								const stats = topicStats[topic.topic_name];
								const colors = [
									'#5B8FF9',
									'#5AD8A6',
									'#5D7092',
									'#F6BD16',
									'#E86452',
									'#6DC8EC',
									'#9270CA',
									'#FF9D4D',
									'#269A99',
									'#FF99C3',
								];
								const color = colors[index % colors.length];
								const isHidden = hiddenTopics.has(topic.topic_name);

								return (
									<Tooltip
										key={topic.topic_name}
										title={
											<div>
												<div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
													{topic.topic_name}
												</div>
												<div>Total: {stats?.total || 0} conversations</div>
												<div>Avg Daily: {stats?.avgDaily || '0'}</div>
												<div>
													Peak: {stats?.peakCount || 0} on {stats?.peakDate || 'N/A'}
												</div>
												<div style={{ marginTop: '4px', fontSize: '11px', opacity: 0.8 }}>
													Click to {isHidden ? 'show' : 'hide'}
												</div>
											</div>
										}
									>
										<div
											onClick={() => toggleTopic(topic.topic_name)}
											onMouseEnter={(e) => {
												e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)';
											}}
											onMouseLeave={(e) => {
												e.currentTarget.style.backgroundColor = 'transparent';
											}}
											style={{
												display: 'flex',
												alignItems: 'center',
												gap: '4px',
												cursor: 'pointer',
												padding: '2px 6px',
												borderRadius: '4px',
												fontSize: '12px',
												opacity: isHidden ? 0.4 : 1,
												textDecoration: isHidden ? 'line-through' : 'none',
												transition: 'opacity 0.2s ease, background-color 0.2s ease',
											}}
										>
											<div
												style={{
													width: '8px',
													height: '8px',
													borderRadius: '50%',
													backgroundColor: isHidden ? '#ccc' : color,
													border: isHidden ? `2px solid ${color}` : 'none',
													boxSizing: 'border-box',
												}}
											/>
											<span>{topic.topic_name}</span>
										</div>
									</Tooltip>
								);
							})}
						</div>

						{/* Chart without legend */}
						<div style={{ height: '250px' }}>
							<Line
								data={allTopicsChartData}
								xField="date"
								yField="count"
								seriesField="topic"
								colorField="topic"
								legend={false} // Hide default legend since we have custom one above
							/>
						</div>
					</div>
				</Spin>
			</Card>

			<List
				header={<Title level={4}>Topics Breakdown</Title>}
				loading={loading}
				grid={{ gutter: 16, xs: 1, sm: 1, md: 2, xl: 2, xxl: 3 }}
				dataSource={allTopics}
				renderItem={(item) => {
					const { positive, neutral, negative } = item.sentiment_breakdown;
					const totalSentiments = positive + neutral + negative;
					const positiveWidth = totalSentiments > 0 ? (positive / totalSentiments) * 100 : 0;
					const negativeWidth = totalSentiments > 0 ? (negative / totalSentiments) * 100 : 0;
					const neutralWidth = 100 - positiveWidth - negativeWidth;

					return (
						<List.Item>
							<Card
								title={item.topic_name}
								extra={<Tag color="blue">{item.total_count} conversations</Tag>}
								style={{ height: '100%' }}
							>
								<Space direction="vertical" style={{ width: '100%' }} size="middle">
									<div>
										<Text strong>Sentiment Breakdown</Text>
										<Tooltip
											title={`Positive: ${positive} | Neutral: ${neutral} | Negative: ${negative}`}
										>
											<div
												style={{
													display: 'flex',
													height: '8px',
													borderRadius: '4px',
													overflow: 'hidden',
													marginTop: '4px',
													backgroundColor: '#f0f0f0',
												}}
											>
												<div style={{ width: `${positiveWidth}%`, backgroundColor: '#52c41a' }} />
												<div style={{ width: `${neutralWidth}%`, backgroundColor: '#faad14' }} />
												<div style={{ width: `${negativeWidth}%`, backgroundColor: '#f5222d' }} />
											</div>
										</Tooltip>
									</div>
									{item.sample_conversations.length > 0 ? (
										<Collapse
											ghost
											accordion
											items={[
												{
													key: '1',
													label: (
														<Text strong>
															Show {item.sample_conversations.length} Sample Conversation
														</Text>
													),
													children: (
														<List
															size="small"
															dataSource={item.sample_conversations}
															pagination={{
																pageSize: 5,
																size: 'small',
																showSizeChanger: false,
															}}
															renderItem={(convo) => (
																<List.Item style={{ padding: '4px 0' }}>
																	<SampleMessage convo={convo} />
																</List.Item>
															)}
														/>
													),
												},
											]}
										/>
									) : (
										<Text type="secondary">No samples available for this period.</Text>
									)}
								</Space>
							</Card>
						</List.Item>
					);
				}}
				locale={{ emptyText: <Empty description="No topics match the selected filters." /> }}
			/>
		</Space>
	);
};

export default IntercomTrends;
